"use client";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import Input, { InputProps } from "@/components/ui/input";
import RouterLink from "@/components/ui/router-link";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ValidateFormItem } from "@/components/ui/validate";
import { useEffect, useRef, useState } from "react";

import {
  Controller,
  useForm,
  FormProvider,
  useWatch,
  useFormContext,
} from "react-hook-form";

import { useScopedI18n } from "@/messages/client";
import FormPhoneAreaCode from "@/components/blocks/Form/FormPhoneAreaCode";
import { LoginParams } from "@/server/type";
import useCountDown from "@/hooks/useCountDown";
import useSWRMutation from "swr/mutation";
import toast from "react-hot-toast";
import { postNextLogin, postPhoneCode } from "@/server/user";
import { useCodeMessage } from "@/hooks/useCodeMessage";
import { useSearchParams } from "next/navigation";
import { Mail, Smartphone } from "lucide-react";
import { useCurrentLocale } from "@/hooks/useCurrentLocale";

export function CodeSMSInput(props: InputProps) {
  const t = useScopedI18n("登录模块");

  const locale = useCurrentLocale();

  const { control, trigger: formTrigger } = useFormContext<LoginParams>();

  const phoneNumber = useWatch({
    control,
    name: "phoneNumber",
  });

  const phoneArea = useWatch({
    control,
    name: "phoneArea",
  });

  const { count, start, isLoading, clear } = useCountDown(60);

  const { isMutating, trigger } = useSWRMutation(
    phoneNumber ? `/api/sms/code/${phoneNumber}` : null,
    () => {
      if (!phoneNumber) return;

      return postPhoneCode({
        phoneNumber,
        phoneArea: phoneArea ?? "86",
        language: locale === "cn" ? "zh" : locale,
      });
    },
    {
      onSuccess: (res) => {
        if (res && "data" in res && res?.data === "success") {
          start();

          toast.success(t("手机验证码发送成功"));
        } else {
          if (res && "errors" in res) {
            toast.error(res?.errors?.[0]?.message ?? "发送失败");
          }
        }
      },
      onError: () => {
        clear();
        toast.error(t("手机验证码发送失败"));
      },
    }
  );

  return (
    <Input
      {...props}
      placeholder={t("请输入验证码")}
      autoComplete="off"
      suffix={
        <Button
          variant="link"
          type="button"
          loading={isMutating}
          disabled={isLoading}
          className="cursor-pointer whitespace-nowrap pl-2 pr-0 text-xs text-[#2967E3] transition-opacity hover:opacity-80"
          onClick={() => {
            if (!phoneNumber) {
              formTrigger("phoneNumber");
              return;
            }

            trigger();
          }}>
          {isLoading
            ? t<any, any>("重新发送", {
                count: count ?? 0,
              })
            : t("获取验证码")}
        </Button>
      }
    />
  );
}

function LoginForm() {
  const t = useScopedI18n("登录模块");

  const toastId = useRef<string>();

  const locale = useCurrentLocale();

  const timer = useRef<NodeJS.Timeout>();

  const redirect_url = useSearchParams().get("redirect_url");

  const [current, setCurrent] = useState("account");

  const [remember, setRemember] = useState(false);

  const codeMessage = useCodeMessage();

  const methods = useForm<LoginParams>();

  const { control, handleSubmit, clearErrors, reset } = methods;

  const { trigger, isMutating } = useSWRMutation(
    "/api/login",
    (_: string, { arg }: { arg: LoginParams }) => {
      const form = new FormData();
      Object.entries(arg).forEach(([key, value]) => {
        form.append(key, value);
      });

      return postNextLogin(form);
    }
  );

  const onSubmit = handleSubmit(async (data) => {
    try {
      toastId.current = toast.loading(t("登录中"));

      // 过滤空字符串
      Object.keys(data).forEach((key) => {
        if (!data[key as keyof typeof data]) {
          delete data[key as keyof typeof data];
        }
      });

      const res = await trigger(data);

      if ("message" in res && res.message === "success") {
        toast.success(t("登录成功"), {
          id: toastId.current,
        });

        timer.current = setTimeout(() => {
          window.location.replace(
            redirect_url ? redirect_url : locale === "cn" ? "/" : `/${locale}`
          );
        }, 1200);

        if ("data" in res) {
          const token = res.data.access_token;

          window.localStorage.setItem("token", token);
        }

        return;
      } else if ("code" in res) {
        toast.error(codeMessage(res.code) || t("登录失败"), {
          id: toastId.current,
        });
      } else if ("message" in res) {
        toast.error(res.message, {
          id: toastId.current,
        });
      } else {
        toast.error(t("登录失败"), {
          id: toastId.current,
        });
      }
    } catch (error) {
      toast.error(t("登录失败"), {
        id: toastId.current,
      });
    }
  });

  useEffect(() => {
    const currentTimer = timer.current;
    return () => {
      currentTimer && clearTimeout(currentTimer);
    };
  }, []);

  return (
    <FormProvider {...methods}>
      <form
        className="w-full flex-shrink-0 rounded-xl bg-white md:w-[27.25rem]"
        onSubmit={onSubmit}>
        <Tabs
          value={current}
          onValueChange={(value) => {
            clearErrors();
            reset();
            setCurrent(value);

            if (value === "phone") {
              methods.setValue("phoneArea", "86");
            }
          }}
          className="w-full">
          {/* <TabsList className="relative hidden h-[70px] border-b border-[#E5E5E5] md:flex">
            <div className="absolute left-1/2 top-1/2 h-6 w-px -translate-x-1/2 -translate-y-1/2 bg-[#E5E5E5]"></div>
            <TabsTrigger
              className="relative flex-1 cursor-pointer py-4 text-lg text-[#a6a6a6] transition-colors after:absolute after:bottom-0 after:left-1/2 after:h-[3px] after:w-[4.5rem] after:-translate-x-1/2 after:bg-transparent after:transition-colors hover:text-black data-[state=active]:border-none data-[state=active]:text-[#202332] data-[state=active]:after:bg-black"
              value="account">
              {t("账号登录")}
            </TabsTrigger>
            <TabsTrigger
              className="relative flex-1 cursor-pointer py-4 text-lg text-[#a6a6a6] transition-colors after:absolute after:bottom-0 after:left-1/2 after:h-[3px] after:w-[4.5rem] after:-translate-x-1/2 after:bg-transparent after:transition-colors hover:text-black data-[state=active]:border-none data-[state=active]:text-[#202332] data-[state=active]:after:bg-black"
              value="phone">
              {t("手机登录")}
            </TabsTrigger>
          </TabsList> */}
          <TabsContent value="account">
            <div className="pb-6 pt-7 md:px-9">
              <div className="mb-5 flex justify-center md:hidden">
                <Image src="/logo-sm.webp" alt="Logo" width={40} height={40} />
              </div>

              <h2 className="text-center text-2xl font-medium text-black">
                {t("南芯账号登录")}
              </h2>

              <div className="mt-12 space-y-5 md:mt-7 md:space-y-6">
                <Controller
                  name="loginIdentifier"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("请输入邮箱地址"),
                    },
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: t("请输入有效的邮箱地址"),
                    },
                  }}
                  render={({ field }) => (
                    <ValidateFormItem name="loginIdentifier">
                      <Input {...field} placeholder={t("邮箱地址")} />
                    </ValidateFormItem>
                  )}
                />

                <Controller
                  name="password"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("请输入密码"),
                    },
                    minLength: {
                      value: 8,
                      message: t("密码长度至少为8位"),
                    },
                  }}
                  render={({ field }) => (
                    <ValidateFormItem name="password">
                      <Input
                        {...field}
                        type="password"
                        autoComplete={remember ? "current-password" : "off"}
                        placeholder={t("密码")}
                      />
                    </ValidateFormItem>
                  )}
                />

                <div className="flex items-center justify-between">
                  <label
                    htmlFor="remember"
                    className="flex cursor-pointer items-center space-x-2">
                    <Checkbox
                      id="remember"
                      checked={remember}
                      onCheckedChange={(checked) =>
                        setRemember(checked as boolean)
                      }
                    />

                    <span className="text-xs text-[#4A4A4A] md:text-sm">
                      {t("记住密码")}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="phone">
            <div className="pb-8 pt-14 md:px-9 md:pb-16 md:pt-7">
              <h2 className="hidden text-center text-2xl font-medium text-black md:block">
                {t("手机验证码登录")}
              </h2>

              <div className="text-center text-2xl font-medium text-[#2A2E3A] md:hidden">
                {t("手机快捷登录")}
              </div>

              <p className="mt-1 text-center text-xs text-[#A6A6A6] md:hidden">
                {t("未注册过的手机号将自动创建账号")}
              </p>

              <div className="mt-7 space-y-6">
                <Controller
                  name="phoneNumber"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("请输入手机号"),
                    },
                    pattern: {
                      value: /^[1-9]\d{1,14}$/,
                      message: t("请输入有效的手机号"),
                    },
                  }}
                  render={({ field }) => (
                    <ValidateFormItem name="phoneNumber">
                      <Input
                        {...field}
                        type="tel"
                        autoComplete="off"
                        placeholder={t("手机号")}
                        prefixs={
                          <Controller
                            name="phoneArea"
                            control={control}
                            render={({ field }) => (
                              <FormPhoneAreaCode
                                value={field.value}
                                onChange={field.onChange}
                              />
                            )}
                          />
                        }
                      />
                    </ValidateFormItem>
                  )}
                />

                <Controller
                  name="smsCode"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("请输入短信验证码"),
                    },
                  }}
                  render={({ field }) => (
                    <ValidateFormItem name="smsCode">
                      <CodeSMSInput {...field} />
                    </ValidateFormItem>
                  )}
                />

                <div className="flex items-center justify-end">
                  <div
                    className="flex items-center space-x-1 text-xs text-[#4A4A4A] md:hidden"
                    onClick={() => {
                      setCurrent("account");
                    }}>
                    <Mail size={14} />

                    <span>{t("账号登录")}</span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
        <div className="pb-16 pt-6 md:px-9 md:pt-0">
          <Button
            className="w-full rounded-md bg-[#E67263] hover:bg-[#cc2016] md:rounded"
            loading={isMutating}
            type="submit">
            {t("登录")}
          </Button>

          <div className="mt-5 hidden items-center justify-center divide-x divide-[#E4E7ED] md:flex">
            <RouterLink
              className="px-2 text-sm text-[#a6a6a6]"
              href="/register">
              {t("立即注册")}
            </RouterLink>

            <RouterLink
              className="px-2 text-sm text-[#a6a6a6]"
              href="/forgot-password">
              {t("忘记密码")}
            </RouterLink>
          </div>

          <div className="mt-5 space-y-5 md:hidden">
            <RouterLink href="/register">
              <Button
                type="button"
                variant="secondary"
                className="w-full rounded-md text-[#CC2016] hover:text-[#E67263]">
                {t("立即注册")}
              </Button>
            </RouterLink>

            {current === "account" && (
              <RouterLink href="/forgot-password">
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full rounded-md text-sm text-[#4371FE]">
                  {t("忘记密码")}?
                </Button>
              </RouterLink>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

function LoginContent() {
  const t = useScopedI18n("登录模块");

  return (
    <div>
      <div className="pb-4">
        <Image src="/icons/union.svg" alt="Union" width={13} height={13} />

        <div className="pl-2 pt-1 text-xl text-[#CC1623] md:text-2xl lg:text-3xl">
          {t("南芯，为效率而生")}
        </div>
      </div>
      <div className="ml-2 inline-block border-b border-[#383838] pb-4">
        <h1 className="text-2xl font-semibold md:text-3xl lg:text-4xl">
          {t("注册MySC账号，获取更多信息")}
        </h1>
      </div>

      <div className="pl-2 pt-10 md:pt-12 lg:pt-20">
        <div className="grid grid-cols-1 gap-x-5 gap-y-10 lg:grid-cols-2">
          <div className="flex items-center space-x-4 text-xl text-[#383838]">
            <Image src="/icons/box.svg" alt="Union" width={34} height={34} />

            <p>{t("申请样品和评估板")}</p>
          </div>
          <div className="flex items-center space-x-4 text-xl text-[#383838]">
            <Image
              src="/icons/document.svg"
              alt="Union"
              width={34}
              height={34}
            />

            <p>{t("下载技术文档")}</p>
          </div>

          <div className="flex items-center space-x-4 text-xl text-[#383838]">
            <Image
              src="/icons/block-diagram.svg"
              alt="Union"
              width={34}
              height={34}
            />

            <p>{t("查看应用框图")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const t = useScopedI18n("登录模块");

  return (
    <div className="flex min-h-[calc(100vh-2.75rem)] flex-col md:min-h-[calc(100vh-7.125rem)]">
      <div className="login_container relative flex-1">
        <div className="content_container">
          <div className="relative flex md:py-14">
            <div className="hidden flex-1 pr-5 md:block md:pr-8 lg:pr-14">
              <LoginContent />
            </div>

            <LoginForm />
          </div>
        </div>

        <div className="absolute bottom-[8%] left-0 right-0 flex w-full items-center justify-center md:hidden">
          <div className="flex items-center text-xs text-[#A6A6A6]">
            {t<any, any>("登录隐私协议描述", {
              privacy: (
                <RouterLink href="/privacy-policy" className="text-[#4371FE]">
                  {t("隐私政策声明")}
                </RouterLink>
              ),
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
